import { Button } from '@hologram-dimension/button';
import { Modal } from '@hologram-dimension/modal';
import clsx from 'clsx/lite';
import SimInventoryBulkSendMessage from 'components/SimInventoryBulkSendMessage/SimInventoryBulkSendMessage';
import { useCallback, useRef, useState, type FC } from 'react';
import type { HologramSimIdsMap, OnSubmit } from 'types/common';
import formatNumber from 'utils/numbers/formatNumber';
import type { CommonModalProps } from '../shared';
import styles from './SimInventoryBulkSendMessage.module.css';

interface SimInventoryBulkSendMessageProps extends CommonModalProps {
  sims: HologramSimIdsMap;
  onSuccess: (sims: Array<HologramSimId>) => void;
  onError: (error?: string) => void;
}

const SimInventoryBulkSendMessageModal: FC<SimInventoryBulkSendMessageProps> = ({
  onClose,
  defaultOpen = false,
  className,
  sims,
  onSuccess,
  onError,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [processing, setIsProcessing] = useState(false);
  const [done, setDone] = useState(false);
  const submitRef = useRef<OnSubmit>();

  const onProcessing = useCallback((value: boolean) => {
    setIsProcessing(value);
  }, []);

  const onDone = useCallback(
    (modifiedSims?: Array<HologramSimId>) => {
      setDone(true);
      if (modifiedSims) {
        onSuccess(modifiedSims);
      }
      onClose();
    },
    [onClose, onSuccess]
  );

  const handleSubmit: React.MouseEventHandler &
    React.MouseEventHandler<HTMLAnchorElement> &
    React.MouseEventHandler<HTMLButtonElement> = async (e) => {
    e.preventDefault();

    if (submitRef.current) {
      await submitRef.current.onSubmit();
    }
  };

  return (
    <Modal
      className={clsx(className, styles.wrapper)}
      header={
        sims.size === 1 ? 'Send message to SIM' : `Send message to ${formatNumber(sims.size)} SIMs`
      }
      onClosed={onClose}
      open={open}
      setOpen={setOpen}
      noContentPadding
      footerActions={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              setOpen(false);
            }}
          >
            {done ? 'Close' : 'Cancel'}
          </Button>
          {!done && (
            <Button loading={processing} onClick={handleSubmit} variant="primary">
              {sims.size === 1 ? 'Send message' : `Send message to ${formatNumber(sims.size)} SIMs`}
            </Button>
          )}
        </>
      }
    >
      <SimInventoryBulkSendMessage
        sims={sims}
        onProcessing={onProcessing}
        ref={submitRef}
        onDone={onDone}
        onError={onError}
      />
    </Modal>
  );
};

export default SimInventoryBulkSendMessageModal;
